import { useCallback, useMemo, useReducer } from 'react'
import type { Task } from '@/src/renderer/lib/db/models'
import type { StagedAction } from '../../../utils/buildTaskUpdate'

export type { StagedAction }

export interface ClarifyState {
  /** Frozen snapshot of inbox tasks at Clarify-Mode entry. */
  taskQueue: Task[]
  /** Task-ID → chosen action. */
  staged: Map<string, StagedAction>
  /** Chronological order in which tasks were staged (append on stage, remove on unstage). */
  stagedOrder: string[]
  /** Task that the action panel currently targets. */
  activeTaskId: string | null
}

type ClarifyEvent =
  | { type: 'stage'; taskId: string; action: StagedAction }
  | { type: 'unstage'; taskId: string }
  | { type: 'setActive'; taskId: string }
  | { type: 'previous' }
  | { type: 'next' }

function pickNextActive(
  queue: Task[],
  stagedSet: Map<string, StagedAction>,
  currentId: string | null,
): string | null {
  if (queue.length === 0) return null
  // Scan forward from the current position (exclusive), wrapping once.
  const curIdx = currentId ? queue.findIndex(t => t.id === currentId) : -1
  for (let i = curIdx + 1; i < queue.length; i++) {
    if (!stagedSet.has(queue[i].id)) return queue[i].id
  }
  for (let i = 0; i <= curIdx; i++) {
    if (!stagedSet.has(queue[i].id)) return queue[i].id
  }
  // Every task staged — keep focus on the last one the user acted on.
  return currentId ?? queue[queue.length - 1].id
}

function reducer(state: ClarifyState, event: ClarifyEvent): ClarifyState {
  switch (event.type) {
    case 'stage': {
      const nextStaged = new Map(state.staged)
      nextStaged.set(event.taskId, event.action)
      const nextOrder = state.stagedOrder.includes(event.taskId)
        ? state.stagedOrder
        : [...state.stagedOrder, event.taskId]
      const nextActive = pickNextActive(state.taskQueue, nextStaged, event.taskId)
      return {
        ...state,
        staged: nextStaged,
        stagedOrder: nextOrder,
        activeTaskId: nextActive,
      }
    }
    case 'unstage': {
      if (!state.staged.has(event.taskId)) return state
      const nextStaged = new Map(state.staged)
      nextStaged.delete(event.taskId)
      return {
        ...state,
        staged: nextStaged,
        stagedOrder: state.stagedOrder.filter(id => id !== event.taskId),
        // Unstaging brings the card back into scope — focus it so the user can re-decide.
        activeTaskId: event.taskId,
      }
    }
    case 'setActive': {
      if (!state.taskQueue.some(t => t.id === event.taskId)) return state
      return { ...state, activeTaskId: event.taskId }
    }
    case 'previous':
    case 'next': {
      if (state.taskQueue.length === 0) return state
      const delta = event.type === 'next' ? 1 : -1
      const curIdx = state.activeTaskId
        ? state.taskQueue.findIndex(t => t.id === state.activeTaskId)
        : -1
      const nextIdx = Math.max(
        0,
        Math.min(state.taskQueue.length - 1, curIdx + delta),
      )
      return { ...state, activeTaskId: state.taskQueue[nextIdx].id }
    }
    default:
      return state
  }
}

function init(tasks: Task[]): ClarifyState {
  return {
    taskQueue: tasks,
    staged: new Map(),
    stagedOrder: [],
    activeTaskId: tasks[0]?.id ?? null,
  }
}

export interface UseClarifyStateResult extends ClarifyState {
  /** Derived live-progress: staged / total, clamped to 0..1. */
  progress: number
  /** IDs rendered in the stack overlay — at most 2, newest first. */
  stackIds: string[]
  /** Task object for `activeTaskId`, or null if queue is empty. */
  activeTask: Task | null
  stageAction: (taskId: string, action: StagedAction) => void
  unstageAction: (taskId: string) => void
  setActive: (taskId: string) => void
  moveToPrevious: () => void
  moveToNext: () => void
}

/**
 * Pure state hook for Quick-Notes Clarify mode. Owns the frozen task queue
 * snapshot, the per-task staged-action map, staging order, and the active
 * task pointer.
 *
 * `tasks` is only read on first render — subsequent changes to the array
 * are ignored to keep the flow stable while the user is deciding.
 */
export function useClarifyState(tasks: Task[]): UseClarifyStateResult {
  const [state, dispatch] = useReducer(reducer, tasks, init)

  const stageAction = useCallback((taskId: string, action: StagedAction) => {
    dispatch({ type: 'stage', taskId, action })
  }, [])

  const unstageAction = useCallback((taskId: string) => {
    dispatch({ type: 'unstage', taskId })
  }, [])

  const setActive = useCallback((taskId: string) => {
    dispatch({ type: 'setActive', taskId })
  }, [])

  const moveToPrevious = useCallback(() => dispatch({ type: 'previous' }), [])
  const moveToNext = useCallback(() => dispatch({ type: 'next' }), [])

  const progress = state.taskQueue.length === 0
    ? 0
    : Math.min(1, state.staged.size / state.taskQueue.length)

  // Stack shows the ≤2 cards IMMEDIATELY BEFORE the active one in queue
  // order — regardless of whether those cards are staged. When the user
  // scrolls the list so the active card sits near the top, these are the
  // cards that just scrolled out of view and the stack overlay previews
  // them. Newest (closest to active) first so depth-0 renders on top.
  const stackIds = useMemo(() => {
    const activeIdx = state.activeTaskId
      ? state.taskQueue.findIndex(t => t.id === state.activeTaskId)
      : -1
    if (activeIdx <= 0) return []
    return state.taskQueue
      .slice(Math.max(0, activeIdx - 2), activeIdx)
      .map(t => t.id)
      .reverse()
  }, [state.activeTaskId, state.taskQueue])

  const activeTask = useMemo(
    () => state.taskQueue.find(t => t.id === state.activeTaskId) ?? null,
    [state.taskQueue, state.activeTaskId],
  )

  return {
    ...state,
    progress,
    stackIds,
    activeTask,
    stageAction,
    unstageAction,
    setActive,
    moveToPrevious,
    moveToNext,
  }
}
